import { Dialog, DialogContent, DialogTitle } from '@mui/material'
import { useState, useMemo } from 'react'
import { getBookingMethodByMentorId } from 'src/clinic/components/ClinicManageBookingsPage/slotItem/bookingUtils/bookMethod'
import { genericHandleChange } from 'src/consumer/helpers/utilFunctions'
import NextVisitHoldSlotConfirmationView from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/NextVisitHoldSlotConfirmationView'

// Import child components
import ConfirmationFooter from './NextVisitSlotSelector/components/ConfirmationFooter'
import DialogHeader from './NextVisitSlotSelector/components/DialogHeader'

// Import utils
import SlotSelectionView from 'src/mentor/components/PrescriptionMakingPage/screeningReportViewer/components/prescriptionMakingPageComponents/NextVisitSlotSelector/components/SlotSelectionView'
import NextVisitSlotSelectionSkipConfirmaion from 'src/mentor/components/TherapyReportMakingPage/components/NextVisitSlotSelectionSkipConfirmaion'
import {
  createBookingPayload,
  handleBookingSuccess,
} from './NextVisitSlotSelector/utils/bookingUtils'

const NextVisitSlotSelector = ({
  open,
  setOpen,
  leaveRoom,
  onClose,
  selectedDate,
  setSelectedDate,
  onSlotSelect,
  roomId = null, // Placeholder for selected doctor/clinic
  currentBookedEventId,
  mentorProfile = null,
  bookingDate,
  currentEventTypeObject = null,
  selectedPatient = null, // Patient info for booking
  refreshData = null, // Function to refresh data after booking
  currentFee = null,
  currentCurrency = null,
  saveReport,
  nextVisitHoldSlotOmissionReason,
  setNextVisitHoldSlotOmissionReason,
  isMarkingAsCompleted,
  holdSlotOmissionDialog, // Promise dialog passed from parent
  clinicLocation = null, // Clinic location for timezone
  bookedSlots = [], // Booked slots to disable overlapping slots
  bookedSlotsLoading = false, // Loading state for booked slots
}) => {
  const [currentView, setCurrentView] = useState('calendar') // 'calendar' or 'confirmation'
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [isBookingLoading, setIsBookingLoading] = useState(false)
  const [selectedEventTypeId, setSelectedEventTypeId] = useState(currentEventTypeObject?.uid || null)

  // Booking form state
  const [bookingFormData, setBookingFormData] = useState({
    currency: currentCurrency || 'inr',
    fee: currentFee || '',
  })

  // Get selected event type object
  const selectedEventTypeObject = useMemo(() => {
    if (!mentorProfile?.eventTypes) return currentEventTypeObject
    return mentorProfile.eventTypes.find(et => et.uid === selectedEventTypeId) || currentEventTypeObject
  }, [mentorProfile, selectedEventTypeId, currentEventTypeObject])

  const handleBookingFormChange = (e) => {
    genericHandleChange(e, setBookingFormData)
  }

  const handleTimeSlotSelect = (slotData) => {
    setSelectedSlot(slotData)
    setCurrentView('confirmation')
  }

  const handleConfirmSlot = async () => {
    console.log('Try to book', {
      selectedSlot,
      selectedPatient,
      mentorProfile,
      selectedEventTypeObject,
    })
    if (!selectedSlot || !selectedPatient || !mentorProfile || !selectedEventTypeObject) {
      return
    }

    setIsBookingLoading(true)

    try {
      // Get booking function based on mentor ID
      let bookSessionFunction = getBookingMethodByMentorId(mentorProfile?.id)

      const response = await bookSessionFunction(
        '/api/secure/meeting/book',
        createBookingPayload({
          mentorProfile,
          currentEventTypeObject: selectedEventTypeObject,
          selectedSlot,
          selectedPatient,
          bookingFormData,
          currentBookedEventId,
        }),
      )

      if (!!response) {
        await handleBookingSuccess({
          onSlotSelect,
          selectedSlot,
          selectedDate,
          refreshData,
          saveReport,
          handleClose,
        })
      } else {
        throw new Error('Error Booking Session')
      }
    } catch (error) {
      console.log('Booking error:', error)
      // You might want to show a toast or error message here
    }

    setIsBookingLoading(false)
  }

  const handleClose = (keepTheNextVisitInput = false) => {
    setCurrentView('calendar')

    setBookingFormData({
      currency: 'inr',
      fee: currentFee || '',
    })
    if (!keepTheNextVisitInput) {
      setSelectedSlot(null)
      setSelectedEventTypeId(currentEventTypeObject?.uid || null)
    }
    onClose(keepTheNextVisitInput)
  }

  const handleBackToCalendar = () => {
    setSelectedSlot(null)
    setCurrentView('calendar')
  }

  if (!currentBookedEventId) {
    return null
  }

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { minHeight: '600px', maxWidth: '675px', width: '100%' } }}
      >
        <DialogTitle>
          <DialogHeader
            currentView={currentView}
            selectedDate={selectedDate}
            bookingDate={bookingDate}
            isBookingLoading={isBookingLoading}
            onBackToCalendar={handleBackToCalendar}
            onSkip={() => handleClose(true)}
            nextVisitHoldSlotOmissionReason={nextVisitHoldSlotOmissionReason}
            setNextVisitHoldSlotOmissionReason={setNextVisitHoldSlotOmissionReason}
            holdSlotOmissionDialog={holdSlotOmissionDialog}
          />
        </DialogTitle>

        <DialogContent 
          style={{ 
            padding: 0, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {currentView === 'calendar' ? (
            <SlotSelectionView
              bookingDate={bookingDate}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              onSlotSelect={handleTimeSlotSelect}
              currentEventTypeObject={selectedEventTypeObject}
              mentorProfile={mentorProfile}
              currentBookedEventId={currentBookedEventId}
              clinicLocation={clinicLocation}
              bookedSlots={bookedSlots}
              bookedSlotsLoading={bookedSlotsLoading}
            />
          ) : (
            // Confirmation View
            <NextVisitHoldSlotConfirmationView
              bookingDate={bookingDate}
              bookingFormData={bookingFormData}
              handleBookingFormChange={handleBookingFormChange}
              selectedSlot={selectedSlot}
              selectedDate={selectedDate}
              currentEventTypeObject={selectedEventTypeObject}
              mentorProfile={mentorProfile}
              selectedEventTypeId={selectedEventTypeId}
              setSelectedEventTypeId={setSelectedEventTypeId}
              clinicLocation={clinicLocation}
            />
          )}
        </DialogContent>
        {currentView === 'confirmation' && (
          <ConfirmationFooter
            onConfirmSlot={handleConfirmSlot}
            isBookingLoading={isBookingLoading}
            bookingFormData={bookingFormData}
            selectedDate={selectedDate}
          />
        )}
      </Dialog>
      <Dialog
        open={holdSlotOmissionDialog?.isOpen || false}
        onClose={() => {
          holdSlotOmissionDialog?.reject()
        }}
      >
        <NextVisitSlotSelectionSkipConfirmaion
          closeModal={() => {
            holdSlotOmissionDialog?.reject()
          }}
          submitForm={async () => {
            handleClose(true)
            holdSlotOmissionDialog?.resolve({ confirmed: true, reason: nextVisitHoldSlotOmissionReason })
          }}
          isSaving={false}
          promptToOpenHoldSlotModal={!open}
          onOpenHoldSlotModal={() => {
            setOpen(true)
          }}
          reportReason={nextVisitHoldSlotOmissionReason}
          setReportReason={setNextVisitHoldSlotOmissionReason}
          leaveRoom={leaveRoom}
          isMarkingAsCompleted={isMarkingAsCompleted}
          skipLeaveRoomInDialog={true}
        />
      </Dialog>
    </>
  )
}

export default NextVisitSlotSelector
